import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AddCardBottomPanel from '../../components/addCardBottomPanel/AddCardBottomPanel';
import CreateCardPopup from '../../components/createCardPopup/CreateCardPopup';
import EditableTranslation from '../../components/editableTranslation/EditableTranslation';
import GeneratingCards from '../../components/generatingCards/GeneratingCards';
import MaterialDropdown from '../../components/materialDropdown/MaterialDropdown';
import SubmitInput from '../../components/submitInput/SubmitInput';
import { useUser } from '../../context/UserContext';
import { useDebounce } from '../../hooks/useDebounce';
import { CardType, Translation } from '../../interfaces/cards';
import { Deck } from '../../interfaces/decks';
import { isPhoneticsSupported } from '../../utils/formatting';
import './AddCardPage.css';

const AddCardPage: React.FC = () => {
  const { userData } = useUser();
  const languageCode = userData?.learning_language_code;
  const [decks, setDecks] = useState<Deck[]>([]);
  const [deckId, setDeckId] = useState<number>();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [phrase, setPhrase] = useState<string>('');
  const [lastTranslatedPhrase, setLastTranslatedPhrase] = useState<string>('');
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('');
  const [audioSpeed, setAudioSpeed] = useState<number>(() => {
    const savedSpeed = localStorage.getItem('audioSpeed');
    return savedSpeed ? Number(savedSpeed) : 0.9;
  });
  const location = useLocation();
  const [manualTargetPhrase, setManualTargetPhrase] = useState<string>('');
  const [manualPhonetic, setManualPhonetic] = useState<string>('');
  const [centerAnimation, setCenterAnimation] = useState<{
    type: 'success' | 'error' | 'warning';
    message: string;
    isVisible: boolean;
  } | null>(null);
  // Card type related state
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);
  const [selectedCardTypeIds, setSelectedCardTypeIds] = useState<number[]>([]);

  // Card generation mode state
  const [inputMode, setInputMode] = useState<
    'auto' | 'manual' | 'media' | 'topic'
  >('auto');
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [extractionType, setExtractionType] = useState<
    'vocab' | 'phrases' | 'sentence_chunks'
  >('phrases');
  const [numCards, setNumCards] = useState<number>(10);
  const [bulkCardId, setBulkCardId] = useState<number | null>(null);
  const [generatedCards, setGeneratedCards] = useState<
    Array<{
      text: string;
      translation: string;
      selected: boolean;
      id: number;
    }>
  >([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [lastProcessedSettings, setLastProcessedSettings] = useState<{
    url: string;
    extractionType: string;
    numCards: number;
  } | null>(null);

  // Topic-based generation state
  const [topicInput, setTopicInput] = useState<string>('');
  const [topicExtractionType, setTopicExtractionType] = useState<
    'vocab' | 'phrases' | 'sentence_chunks'
  >('phrases');
  const [topicNumCards, setTopicNumCards] = useState<number>(10);
  const [lastProcessedTopicSettings, setLastProcessedTopicSettings] = useState<{
    topic: string;
    extractionType: string;
    numCards: number;
  } | null>(null);

  const showCenterAnimation = (
    type: 'success' | 'error' | 'warning',
    message: string
  ) => {
    setCenterAnimation({ type, message, isVisible: true });

    // Different timeouts for different types
    const timeout = type === 'success' ? 1200 : 1800; // Success faster, errors/warnings slower
    const hideDelay = type === 'success' ? 200 : 300; // Faster cleanup for success

    setTimeout(() => {
      setCenterAnimation((prev) =>
        prev ? { ...prev, isVisible: false } : null
      );
      // Remove from state after animation completes
      setTimeout(() => setCenterAnimation(null), hideDelay);
    }, timeout);
  };

  // Fetch all available card types
  const fetchCardTypes = useCallback(async () => {
    try {
      const response = await axios.get('/api/cards/types');
      setCardTypes(response.data);
    } catch (error) {
      console.error('Error fetching card types:', error);
    }
  }, []);

  // Update selected card types when deck changes
  const updateSelectedCardTypes = useCallback((deck: Deck | null) => {
    if (deck && deck.card_types) {
      const deckCardTypeIds = deck.card_types.map((ct) => ct.id);
      setSelectedCardTypeIds(deckCardTypeIds);
    } else {
      setSelectedCardTypeIds([]);
    }
  }, []);

  useEffect(() => {
    const state = location.state as { deckId?: number };
    if (location.state && state.deckId) {
      setDeckId(state.deckId);
    }
  }, [location.state]);

  // Fetch card types on component mount
  useEffect(() => {
    fetchCardTypes();
  }, [fetchCardTypes]);

  const fetchDecks = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/decks/language_code/${languageCode}`
      );

      const data: Deck[] = response.data;
      setDecks(data);

      if (location.state && location.state.deckId) {
        setDeckId(location.state.deckId);
      } else {
        setDeckId(data[0]?.id);
      }
    } catch (error) {
      console.error('Error fetching decks:', error);
    }
  }, [location.state, languageCode]);

  useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  const handleDeckChange = (id: string) => {
    const selectedId = parseInt(id, 10);
    setDeckId(selectedId);
  };

  const fetchDeck = useCallback(async () => {
    try {
      const response = await axios.get(`/api/decks/${deckId}`);
      const data = response.data;
      setDeck(data);
      // Update selected card types when deck changes
      updateSelectedCardTypes(data);
    } catch (error) {
      console.error('Error fetching deck details:', error);
    }
  }, [deckId, updateSelectedCardTypes]);

  // Fetch deck details
  useEffect(() => {
    if (deckId) {
      fetchDeck();
    }
  }, [fetchDeck, deckId]);

  const handleTranslate = async () => {
    setTranslations([]);
    try {
      setLoading(true);

      const translateData = {
        sentence: phrase,
        language_code: deck?.language_code,
      };

      const response = await axios.post(
        '/api/generate/text/translations',
        translateData
      );

      const translatedResponse: Translation[] = response.data;

      const translationsWithIDs = translatedResponse.map((item, index) => ({
        ...item,
        id: index,
        selected: false,
      }));

      setTranslations(translationsWithIDs);
      setLastTranslatedPhrase(phrase); // Update last translated phrase
    } catch (error: any) {
      console.error('Error translating phrase:', error);
      const errorMessage =
        error?.response?.data?.detail || 'An error occurred while translating';
      showCenterAnimation('error', `Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (id: number) => {
    const updatedTranslations = translations.map((translation) => {
      if (translation.id === id) {
        // If clicking on selected item, deselect it
        return { ...translation, selected: !translation.selected };
      } else {
        // Deselect all other items (single selection only)
        return { ...translation, selected: false };
      }
    });
    setTranslations(updatedTranslations);
  };

  const handleSaveTranslation = async (id: number, newTranslation: string) => {
    if (newTranslation !== translations[id].translation) {
      try {
        // First, check if phonetic needs to be updated
        const updatePhonetic = translations.find((t) => t.id === id)?.phonetic;

        if (updatePhonetic) {
          const response = await axios.post('/api/generate/phonetics', {
            sentence: newTranslation,
            language_code: deck?.language_code,
          });
          const data = response.data;

          // Update both translation and phonetic in one state update
          setTranslations(
            translations.map((translation) =>
              translation.id === id
                ? {
                    ...translation,
                    translation: newTranslation,
                    phonetic: data.phonetic,
                  }
                : translation
            )
          );
        } else {
          // Update only the translation if phonetic does not need updating
          setTranslations(
            translations.map((translation) =>
              translation.id === id
                ? {
                    ...translation,
                    translation: newTranslation,
                  }
                : translation
            )
          );
        }
      } catch (error: any) {
        console.error('Failed to save translation:', error);
        const errorMessage =
          error?.response?.data?.detail ||
          'An error occurred while saving the translation';
        showCenterAnimation('error', errorMessage);
      }
    }
  };

  const handleAddCards = async () => {
    const selectedItems = translations.filter(
      (translation) => translation.selected
    );

    if (selectedItems.length === 0) {
      showCenterAnimation('warning', 'Please select at least one card to add.');
      return;
    }

    try {
      const requestBody = selectedItems.map((item) => ({
        deck_id: deck?.id,
        native_language: phrase,
        target_language: item.translation,
        phonetic: item.phonetic,
        formality: item.formality,
        voice_id: selectedVoiceId,
        speed: audioSpeed,
        card_type_ids:
          selectedCardTypeIds.length > 0 ? selectedCardTypeIds : undefined,
      }));

      await axios.post(`/api/cards/deck/${deckId}`, requestBody);
      showCenterAnimation('success', 'Card Added!');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        'An error occurred while adding the cards';
      showCenterAnimation('error', `Error adding cards: ${errorMessage}`);
    } finally {
      // Reset state after attempting to add cards
      setPhrase('');
      setTranslations([]);
      setLastTranslatedPhrase('');
    }
  };

  const handleManualAdd = async () => {
    if (!manualTargetPhrase || !phrase) {
      showCenterAnimation('warning', 'Please fill in both languages');
      return;
    }

    try {
      const requestBody = [
        {
          deck_id: deck?.id,
          native_language: phrase,
          target_language: manualTargetPhrase,
          phonetic: manualPhonetic,
          formality: 'casual',
          voice_id: selectedVoiceId,
          speed: audioSpeed,
          card_type_ids:
            selectedCardTypeIds.length > 0 ? selectedCardTypeIds : undefined,
        },
      ];

      await axios.post(`/api/cards/deck/${deckId}`, requestBody);
      showCenterAnimation('success', 'Card Added!');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        'An error occurred while adding the cards';
      showCenterAnimation('error', `Error adding cards: ${errorMessage}`);
    } finally {
      // Reset state after attempting to add cards
      setPhrase('');
      setTranslations([]);
      setManualTargetPhrase('');
      setManualPhonetic('');
    }
  };

  const handleClear = () => {
    setPhrase('');
    setTranslations([]);
    setLastTranslatedPhrase('');
    setManualTargetPhrase('');
    setManualPhonetic('');
    setYoutubeUrl('');
    setGeneratedCards([]);
    setBulkCardId(null);
    setIsGenerating(false);
    setLastProcessedSettings(null);
    setTopicInput('');
    setLastProcessedTopicSettings(null);
  };

  // YouTube bulk generation functions
  const handleYoutubeProcess = async () => {
    if (!youtubeUrl.trim()) {
      showCenterAnimation('warning', 'Please enter a YouTube URL');
      return;
    }

    try {
      setIsGenerating(true);
      // Clear existing generated cards when starting new request
      setGeneratedCards([]);
      setBulkCardId(null);

      // Step 1: Create media from YouTube URL
      const mediaResponse = await axios.post('/api/media', {
        youtube_url: youtubeUrl,
        title: 'YouTube Video Cards',
      });

      const mediaId = mediaResponse.data.id;

      // Step 2: Start bulk card generation
      const bulkRequestData = {
        media_id: mediaId,
        num_items: numCards,
        extraction_type: extractionType,
      };

      const bulkResponse = await axios.post('/api/cards/bulk', bulkRequestData);

      setBulkCardId(bulkResponse.data.id);

      // Step 3: Start polling for results
      pollForBulkCards(bulkResponse.data.id);
    } catch (error: any) {
      console.error('Error processing YouTube:', error);
      const errorMessage =
        error?.response?.data?.detail ||
        'An error occurred while processing the YouTube video';
      showCenterAnimation('error', `Error: ${errorMessage}`);
      setIsGenerating(false);
    }
  };

  const pollForBulkCards = async (bulkCardId: number) => {
    const maxAttempts = 30; // 30 attempts * 2 seconds = 60 seconds max
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        const response = await axios.get(`/api/cards/bulk/${bulkCardId}`);

        // Check if still processing (status code 202)
        if (response.status === 202 || response.data.status === 'processing') {
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000); // Poll every 2 seconds
          } else {
            showCenterAnimation(
              'error',
              'Card generation timed out. Please try again.'
            );
            setIsGenerating(false);
          }
        } else if (response.status === 200) {
          // Cards are ready
          const cardData = response.data.card_data || [];

          const cardsWithSelection = cardData.map(
            (card: any, index: number) => ({
              text: card.text,
              translation: card.translation,
              selected: false,
              id: index,
            })
          );

          setGeneratedCards(cardsWithSelection);
          setIsGenerating(false);
          // Save the current settings as processed
          setLastProcessedSettings({
            url: youtubeUrl,
            extractionType: extractionType,
            numCards: numCards,
          });
          showCenterAnimation('success', `Generated ${cardData.length} cards!`);
        }
      } catch (error: any) {
        console.error('Error polling for bulk cards:', error);

        // If it's a 202 response, continue polling
        if (error.response?.status === 202) {
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            showCenterAnimation(
              'error',
              'Card generation timed out. Please try again.'
            );
            setIsGenerating(false);
          }
        } else {
          showCenterAnimation('error', 'Error checking card generation status');
          setIsGenerating(false);
        }
      }
    };

    poll();
  };

  const handleGeneratedCardToggle = (id: number) => {
    setGeneratedCards((prev) =>
      prev.map((card) =>
        card.id === id ? { ...card, selected: !card.selected } : card
      )
    );
  };

  const handleAddGeneratedCards = async () => {
    const selectedCards = generatedCards.filter((card) => card.selected);

    if (selectedCards.length === 0) {
      showCenterAnimation('warning', 'Please select at least one card to add.');
      return;
    }

    try {
      // Build bulk request body for all selected cards
      const requestBody = selectedCards.map((card) => ({
        deck_id: deck?.id,
        native_language: card.translation,
        target_language: card.text,
        phonetic: '', // Will be generated on backend via background task
        formality: 'casual',
        voice_id: selectedVoiceId,
        speed: audioSpeed,
        card_type_ids:
          selectedCardTypeIds.length > 0 ? selectedCardTypeIds : undefined,
      }));

      // Send all cards in a single bulk request
      await axios.post(`/api/cards/deck/${deckId}`, requestBody);

      showCenterAnimation(
        'success',
        `Added ${selectedCards.length} card${selectedCards.length > 1 ? 's' : ''}!`
      );
      // Clear the generated cards and input after adding
      setGeneratedCards([]);
      setBulkCardId(null);
      setYoutubeUrl('');
      setTopicInput('');
      setLastProcessedTopicSettings(null);
      setLastProcessedSettings(null);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        'An error occurred while adding the cards';
      showCenterAnimation('error', `Error adding cards: ${errorMessage}`);
    }
  };

  // Check if current settings match the last processed settings
  const hasSettingsChanged = () => {
    if (!lastProcessedSettings) return true;
    return (
      lastProcessedSettings.url !== youtubeUrl ||
      lastProcessedSettings.extractionType !== extractionType ||
      lastProcessedSettings.numCards !== numCards
    );
  };

  // Check if topic settings have changed
  const hasTopicSettingsChanged = () => {
    if (!lastProcessedTopicSettings) return true;
    return (
      lastProcessedTopicSettings.topic !== topicInput ||
      lastProcessedTopicSettings.extractionType !== topicExtractionType ||
      lastProcessedTopicSettings.numCards !== topicNumCards
    );
  };

  // Topic-based generation functions
  const handleTopicProcess = async () => {
    if (!topicInput.trim()) {
      showCenterAnimation('warning', 'Please enter a topic');
      return;
    }

    if (!languageCode) {
      showCenterAnimation('warning', 'No language selected');
      return;
    }

    try {
      setIsGenerating(true);
      // Clear existing generated cards when starting new request
      setGeneratedCards([]);

      // Start topic card generation
      const topicRequestData = {
        topic: topicInput,
        language_code: languageCode,
        num_items: topicNumCards,
        extraction_type: topicExtractionType,
      };

      const topicResponse = await axios.post(
        '/api/cards/topic',
        topicRequestData
      );

      // Start polling for results
      pollForTopicCards(topicResponse.data.id);
    } catch (error: any) {
      console.error('Error processing topic:', error);
      const errorMessage =
        error?.response?.data?.detail ||
        'An error occurred while generating cards from topic';
      showCenterAnimation('error', `Error: ${errorMessage}`);
      setIsGenerating(false);
    }
  };

  const pollForTopicCards = async (topicCardId: number) => {
    const maxAttempts = 30; // 30 attempts * 2 seconds = 60 seconds max
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        const response = await axios.get(`/api/cards/topic/${topicCardId}`);

        // Check if still processing (status code 202)
        if (response.status === 202 || response.data.status === 'processing') {
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000); // Poll every 2 seconds
          } else {
            showCenterAnimation(
              'error',
              'Card generation timed out. Please try again.'
            );
            setIsGenerating(false);
          }
        } else if (response.status === 200) {
          // Cards are ready
          const cardData = response.data.card_data || [];

          const cardsWithSelection = cardData.map(
            (card: any, index: number) => ({
              text: card.text,
              translation: card.translation,
              selected: false,
              id: index,
            })
          );

          setGeneratedCards(cardsWithSelection);
          setIsGenerating(false);
          // Save the current settings as processed
          setLastProcessedTopicSettings({
            topic: topicInput,
            extractionType: topicExtractionType,
            numCards: topicNumCards,
          });
          showCenterAnimation('success', `Generated ${cardData.length} cards!`);
        }
      } catch (error: any) {
        console.error('Error polling for topic cards:', error);

        // If it's a 202 response, continue polling
        if (error.response?.status === 202) {
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            showCenterAnimation(
              'error',
              'Card generation timed out. Please try again.'
            );
            setIsGenerating(false);
          }
        } else {
          showCenterAnimation('error', 'Error checking card generation status');
          setIsGenerating(false);
        }
      }
    };

    poll();
  };

  const debouncedUpdatePhonetic = useDebounce(async (value: string) => {
    if (!deck?.language_code || !value) return;

    try {
      const response = await axios.post('/api/generate/phonetics', {
        sentence: value,
        language_code: deck.language_code,
      });
      const data = response.data;
      setManualPhonetic(data.phonetic);
    } catch (error) {
      console.error('Error fetching phonetic:', error);
    }
  }, 300);

  // Handle input mode change - clear generated cards when switching between media/topic
  const handleInputModeChange = (
    newMode: 'auto' | 'manual' | 'media' | 'topic'
  ) => {
    // Clear generated cards when switching between media and topic modes
    if (
      (inputMode === 'media' && newMode === 'topic') ||
      (inputMode === 'topic' && newMode === 'media')
    ) {
      setGeneratedCards([]);
    }
    setInputMode(newMode);
  };

  // Text to use for voice selection based on current mode
  const voiceText =
    inputMode === 'manual'
      ? manualTargetPhrase
      : inputMode === 'media' || inputMode === 'topic'
        ? generatedCards.find((c) => c.selected)?.text
        : translations.find((t) => t.selected)?.translation;

  return (
    <>
      <div className="phn-page-container">
        <div className="add-card-content">
          {/* Tab navigation with Clear button */}
          <div className="tabs-and-clear-container">
            <div className="card-generation-tabs">
              <button
                className={`tab-button ${inputMode === 'auto' ? 'active' : ''}`}
                onClick={() => handleInputModeChange('auto')}
                type="button"
              >
                Auto
              </button>
              <button
                className={`tab-button ${inputMode === 'topic' ? 'active' : ''}`}
                onClick={() => handleInputModeChange('topic')}
                type="button"
              >
                Topic
              </button>
              <button
                className={`tab-button ${inputMode === 'media' ? 'active' : ''}`}
                onClick={() => handleInputModeChange('media')}
                type="button"
              >
                Media
              </button>
              <button
                className={`tab-button ${inputMode === 'manual' ? 'active' : ''}`}
                onClick={() => handleInputModeChange('manual')}
                type="button"
              >
                Manual
              </button>
            </div>
            <button
              onClick={handleClear}
              className="clear-button"
              type="button"
              disabled={
                inputMode === 'auto'
                  ? !phrase && translations.length === 0
                  : inputMode === 'manual'
                    ? !phrase && !manualTargetPhrase
                    : inputMode === 'topic'
                      ? !topicInput && generatedCards.length === 0
                      : !youtubeUrl && generatedCards.length === 0
              }
            >
              Clear
            </button>
          </div>

          {inputMode === 'auto' ? (
            <div className="input-container">
              <SubmitInput
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    phrase &&
                    phrase !== lastTranslatedPhrase
                  ) {
                    handleTranslate();
                  }
                }}
                placeholder="Type something to translate"
                disabled={loading}
                inputType="textarea"
                rows={1}
                onSubmit={handleTranslate}
                showSubmitButton={true}
                submitDisabled={!phrase || phrase === lastTranslatedPhrase}
                loading={loading}
              />
            </div>
          ) : inputMode === 'manual' ? (
            <div className="manual-input-container">
              <SubmitInput
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder="Native language"
                disabled={loading}
                inputType="textarea"
                rows={1}
                showSubmitButton={false}
                loading={false}
              />
              <SubmitInput
                value={manualTargetPhrase}
                onChange={(e) => {
                  const value = e.target.value;
                  setManualTargetPhrase(value);
                  if (isPhoneticsSupported(deck?.language_code || '')) {
                    debouncedUpdatePhonetic(value);
                  }
                }}
                placeholder="Target language"
                disabled={loading}
                inputType="textarea"
                rows={1}
                showSubmitButton={false}
                loading={false}
              />
              {isPhoneticsSupported(deck?.language_code || '') && (
                <SubmitInput
                  value={manualPhonetic}
                  onChange={(e) => setManualPhonetic(e.target.value)}
                  placeholder="Phonetics (optional)"
                  disabled={loading}
                  inputType="textarea"
                  rows={1}
                  showSubmitButton={false}
                  loading={false}
                />
              )}
            </div>
          ) : inputMode === 'topic' ? (
            <div className="topic-container">
              <SubmitInput
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    topicInput.trim() &&
                    (generatedCards.length === 0 || hasTopicSettingsChanged())
                  ) {
                    handleTopicProcess();
                  }
                }}
                placeholder="Enter a topic (e.g., Ordering food)"
                disabled={isGenerating}
                inputType="input"
                onSubmit={handleTopicProcess}
                showSubmitButton={true}
                submitDisabled={
                  !topicInput.trim() ||
                  (generatedCards.length > 0 && !hasTopicSettingsChanged())
                }
                loading={isGenerating}
              />

              {/* Topic controls */}
              <div className="youtube-controls">
                <MaterialDropdown
                  label="Type"
                  value={topicExtractionType}
                  options={[
                    { value: 'phrases', label: 'Phrases' },
                    { value: 'vocab', label: 'Vocabulary' },
                    { value: 'sentence_chunks', label: 'Sentence Chunks' },
                  ]}
                  onChange={(value) =>
                    setTopicExtractionType(
                      value as 'vocab' | 'phrases' | 'sentence_chunks'
                    )
                  }
                  disabled={isGenerating}
                  size="small"
                  fullWidth={false}
                />

                <MaterialDropdown
                  label="Number"
                  value={topicNumCards}
                  options={[
                    { value: 10, label: '10 cards' },
                    { value: 20, label: '20 cards' },
                    { value: 30, label: '30 cards' },
                  ]}
                  onChange={(value) => setTopicNumCards(Number(value))}
                  disabled={isGenerating}
                  size="small"
                  fullWidth={false}
                />
              </div>
            </div>
          ) : (
            <div className="youtube-container">
              <SubmitInput
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="Enter YouTube URL"
                disabled={isGenerating}
                inputType="input"
                onSubmit={handleYoutubeProcess}
                showSubmitButton={true}
                submitDisabled={
                  !youtubeUrl.trim() ||
                  (generatedCards.length > 0 && !hasSettingsChanged())
                }
                loading={isGenerating}
              />

              {/* YouTube controls */}
              <div className="youtube-controls">
                <MaterialDropdown
                  label="Type"
                  value={extractionType}
                  options={[
                    { value: 'phrases', label: 'Phrases' },
                    { value: 'vocab', label: 'Vocabulary' },
                    { value: 'sentence_chunks', label: 'Sentence Chunks' },
                  ]}
                  onChange={(value) =>
                    setExtractionType(
                      value as 'vocab' | 'phrases' | 'sentence_chunks'
                    )
                  }
                  disabled={isGenerating}
                  size="small"
                  fullWidth={false}
                />

                <MaterialDropdown
                  label="Number"
                  value={numCards}
                  options={[
                    { value: 10, label: '10 cards' },
                    { value: 20, label: '20 cards' },
                    { value: 30, label: '30 cards' },
                  ]}
                  onChange={(value) => setNumCards(Number(value))}
                  disabled={isGenerating}
                  size="small"
                  fullWidth={false}
                />
              </div>
            </div>
          )}

          {/* Cards section - scrollable area */}
          <div className="cards-section">
            {/* Loading states */}
            {inputMode === 'auto' && loading && (
              <GeneratingCards message="Generating translations..." />
            )}
            {inputMode === 'media' && isGenerating && (
              <GeneratingCards
                message={
                  bulkCardId
                    ? 'Generating cards from video transcript...'
                    : 'Processing YouTube video and generating cards...'
                }
                stage={bulkCardId ? 'generating' : 'processing'}
              />
            )}
            {inputMode === 'topic' && isGenerating && (
              <GeneratingCards
                message="Generating cards for topic..."
                stage="generating"
              />
            )}

            {/* Cards display */}
            {((inputMode === 'auto' && translations.length > 0) ||
              (inputMode === 'media' && generatedCards.length > 0) ||
              (inputMode === 'topic' && generatedCards.length > 0)) && (
              <>
                <h4 className="translation-list-header">
                  Select cards to add:
                </h4>
                <div className="translation-list">
                  {inputMode === 'auto'
                    ? translations.map((translation) => (
                        <div
                          key={translation.id}
                          className={`phn-white-card translation-item ${translation.selected ? 'selected' : ''}`}
                          onClick={() => handleCheckboxChange(translation.id)}
                        >
                          <div className="translation-details">
                            <EditableTranslation
                              translation={translation.translation}
                              onSave={(newTranslation) =>
                                handleSaveTranslation(
                                  translation.id,
                                  newTranslation
                                )
                              }
                            />
                            <div className="phonetic">
                              {translation.phonetic}
                            </div>
                            <div className="formality">
                              {translation.formality}
                            </div>
                          </div>
                        </div>
                      ))
                    : generatedCards.map((card) => (
                        <div
                          key={card.id}
                          className={`phn-white-card translation-item ${card.selected ? 'selected' : ''}`}
                          onClick={() => handleGeneratedCardToggle(card.id)}
                        >
                          <div className="translation-details">
                            <div className="generated-card-text">
                              {card.text}
                            </div>
                            <div className="generated-card-translation">
                              {card.translation}
                            </div>
                          </div>
                        </div>
                      ))}
                </div>
              </>
            )}
          </div>

          {/* Bottom action bar - always visible */}
          <div className="bottom-panel-container">
            <AddCardBottomPanel
              deckId={deckId}
              decks={decks}
              onDeckChange={handleDeckChange}
              translations={translations}
              manualTargetPhrase={manualTargetPhrase}
              onAddCards={
                inputMode === 'media' || inputMode === 'topic'
                  ? handleAddGeneratedCards
                  : inputMode === 'manual'
                    ? handleManualAdd
                    : handleAddCards
              }
              deck={deck}
              voiceText={voiceText}
              onVoiceChange={(voiceId) => setSelectedVoiceId(voiceId)}
              onSpeedChange={(speed) => setAudioSpeed(speed)}
              cardTypes={cardTypes}
              selectedCardTypeIds={selectedCardTypeIds}
              onCardTypeChange={setSelectedCardTypeIds}
              inputMode={inputMode}
              generatedCards={generatedCards}
            />
          </div>
        </div>
      </div>
      {centerAnimation && (
        <CreateCardPopup
          type={centerAnimation.type}
          message={centerAnimation.message}
          isVisible={centerAnimation.isVisible}
        />
      )}
    </>
  );
};

export default AddCardPage;
